import { DAMAGE_AMOUNTS, DEPRECIATION_CATEGORIES, SIMULATOR_ASSESSMENTS, SIMULATOR_YEARS, } from './catalog.js';
const SEVERITY_RANK = { EXCELLENT: 0, MINOR: 1, MODERATE: 2, MAJOR: 3, SEVERE: 4 };
const GRADE_RANK = { G7: 0, G4: 1, G3: 2, G2: 3, G1: 4, G0: 5 };
const SERVICE_RANK = { full: 0, partial: 1, none: 2 };
const clone = (value) => JSON.parse(JSON.stringify(value));
export const optionByKey = (list, key) => list.find((option) => option.key === key) ?? list[0];
/** Brand equity and powertrain points, as a fraction (0.03 = 3 points). Trim adds nothing by request. */
export const extraPoints = (values, inputs) => optionByKey(values.brand, inputs.equity).pts + optionByKey(values.fuel, inputs.fuel).pts;
/** The category ladder with the extra points spread over the first years, each year clamped to [0, 95%]. */
export const ladderFor = (values, cat, extra) => values.cat[cat].map((rate, index) => {
    const uplift = index < values.spread.length ? extra * (values.spread[index] ?? 0) : 0;
    // Rounded away from float noise (0.1 + 0.05 is 0.15000000000000002), so a ladder prints as entered.
    return Math.round(Math.max(0, Math.min(0.95, rate + uplift)) * 1e9) / 1e9;
});
/** The share of the new price left after `n` years on a ladder (compounded). */
export const leftAfter = (ladder, n) => {
    let left = 1;
    for (let index = 0; index < n; index += 1) {
        left *= 1 - (ladder[index] ?? 0);
    }
    return left;
};
export const prepareCar = (values, inputs) => {
    const age = Math.max(1, Math.min(SIMULATOR_YEARS, Math.round(inputs.age) || 1));
    const extra = extraPoints(values, inputs);
    return {
        ...clone(inputs),
        age,
        price: Math.max(0, inputs.price),
        odometer: Math.max(0, inputs.odometer),
        owners: Math.max(0, Math.round(inputs.owners)),
        extra,
        ladder: ladderFor(values, inputs.cat, extra),
        hv: optionByKey(values.fuel, inputs.fuel).hv,
        kmPerYear: Math.max(0, inputs.odometer) / age,
    };
};
/** One year, in the pricing tool's order, with the proposal's factors in their place. */
export const valueAt = (values, car, n) => {
    const depLeft = leftAfter(car.ladder, n);
    const odometer = car.kmPerYear * n;
    const expectedKm = values.km.allowance * n;
    const excess = Math.max(0, odometer - expectedKm);
    const steps = values.km.step > 0 ? Math.ceil(excess / values.km.step) : 0;
    const mileage = steps * values.km.rate;
    const owner = Math.max(0, car.owners - values.owners.allowance) * values.owners.rate;
    const base = car.price * depLeft * (1 - mileage) * (1 - owner);
    const gradeRate = values.grade[car.table][car.grade];
    const sevRates = {};
    let inspection = gradeRate;
    for (const assessment of SIMULATOR_ASSESSMENTS) {
        const rate = assessment.key === 'hybrid' && !car.hv ? 0 : values.sev[car.sev[assessment.key]];
        sevRates[assessment.key] = rate;
        inspection += rate;
    }
    const amounts = DAMAGE_AMOUNTS[car.table];
    const fixed = (amounts.g1[car.dmg1] ?? 0) + (amounts.g2[car.dmg2] ?? 0);
    const inspected = base * (1 - inspection) - fixed;
    const serviceRate = optionByKey(values.service, car.service).pct;
    const usageRate = optionByKey(values.usage, car.usage).pct;
    const afterService = inspected * (1 + serviceRate);
    const afterUsage = afterService * (1 + usageRate);
    const faceliftRate = car.facelift ? values.facelift : 0;
    const customsRate = car.customs ? values.customs : 0;
    const afterFace = afterUsage * (1 - faceliftRate);
    const afterCustoms = afterFace * (1 - customsRate);
    const specsRate = optionByKey(values.specs, car.specs).pct;
    const warrantyRate = car.warranty ? values.warranty : 0;
    const agencyRate = optionByKey(values.agency, car.agency).pct;
    const afterSpecs = afterCustoms * (1 + specsRate);
    const afterWarranty = afterSpecs * (1 + warrantyRate);
    const afterAgency = afterWarranty * (1 + agencyRate);
    const marketRate = optionByKey(values.demand, car.demand).pct + optionByKey(values.supply, car.supply).pct;
    const afterMarket = afterAgency * (1 + marketRate);
    const rounding = values.rounding > 0 ? values.rounding : 1;
    const appraisal = Math.max(0, Math.round(afterMarket / rounding) * rounding);
    return {
        depLeft,
        dep: 1 - depLeft,
        odometer,
        expectedKm,
        excess,
        steps,
        mileage,
        owner,
        base,
        gradeRate,
        sevRates,
        inspection,
        fixed,
        inspected,
        blocked: inspected < 0,
        serviceRate,
        usageRate,
        afterService,
        afterUsage,
        faceliftRate,
        customsRate,
        afterFace,
        afterCustoms,
        specsRate,
        warrantyRate,
        agencyRate,
        afterSpecs,
        afterWarranty,
        afterAgency,
        marketRate,
        afterMarket,
        appraisal,
        left: car.price > 0 ? appraisal / car.price : 0,
    };
};
/** The same car, clean: full inspection, no damage, full service, driven within the allowance. */
export const bestOf = (values, car) => {
    const best = clone(car);
    best.kmPerYear = Math.min(car.kmPerYear, values.km.allowance);
    best.owners = Math.min(car.owners, values.owners.allowance);
    best.grade = 'G7';
    best.sev = { interior: 'EXCELLENT', body: 'EXCELLENT', engine: 'EXCELLENT', hybrid: 'EXCELLENT', tires: 'EXCELLENT' };
    best.dmg1 = 0;
    best.dmg2 = 0;
    best.service = 'full';
    return best;
};
/** The worst-case profile, element by element never better than what the user entered. */
export const worstOf = (values, car) => {
    const worst = clone(car);
    const worse = (a, b) => (SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b);
    worst.kmPerYear = Math.max(car.kmPerYear, values.worst.kmPerYear);
    worst.grade = GRADE_RANK[car.grade] >= GRADE_RANK[values.worst.grade] ? car.grade : values.worst.grade;
    worst.sev = {
        interior: worse(car.sev.interior, values.worst.sev),
        body: worse(car.sev.body, values.worst.sev),
        engine: worse(car.sev.engine, values.worst.sev),
        hybrid: worse(car.sev.hybrid, values.worst.sev),
        tires: worse(car.sev.tires, values.worst.sev),
    };
    worst.service = (SERVICE_RANK[car.service] ?? 0) >= (SERVICE_RANK[values.worst.service] ?? 0) ? car.service : values.worst.service;
    return worst;
};
const onLadder = (values, car, cat) => ({
    ...clone(car),
    cat,
    ladder: ladderFor(values, cat, car.extra),
});
/** Seven years of best, this car and worst, plus this car on each of the three categories at its age. */
export const simulate = (values, inputs) => {
    const car = prepareCar(values, inputs);
    const best = bestOf(values, car);
    const worst = worstOf(values, car);
    const rows = [];
    for (let n = 1; n <= SIMULATOR_YEARS; n += 1) {
        rows.push({ n, best: valueAt(values, best, n), car: valueAt(values, car, n), worst: valueAt(values, worst, n) });
    }
    const byCategory = DEPRECIATION_CATEGORIES.map(({ key }) => ({
        cat: key,
        best: valueAt(values, onLadder(values, best, key), car.age),
        car: valueAt(values, onLadder(values, car, key), car.age),
        worst: valueAt(values, onLadder(values, worst, key), car.age),
    }));
    return { car, best, worst, rows, at: rows[car.age - 1], byCategory };
};
export const outputsOf = (result) => {
    const byCategory = {};
    for (const comparison of result.byCategory) {
        byCategory[comparison.cat] = { best: comparison.best.appraisal, car: comparison.car.appraisal, worst: comparison.worst.appraisal };
    }
    return {
        simulatorPrice: result.at.car.appraisal,
        bestPrice: result.at.best.appraisal,
        worstPrice: result.at.worst.appraisal,
        left: result.at.car.left,
        bestLeft: result.at.best.left,
        worstLeft: result.at.worst.left,
        blocked: result.at.car.blocked,
        byCategory,
    };
};
//# sourceMappingURL=model.js.map